import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { createHttpClient } from '@piaas/piaas-ui-toolkit/http';
import { v4 as uuidv4 } from 'uuid';

import { startLoginRedirect } from '../auth/interactiveAuth';
import { loginRequest } from '../auth/msalConfig';
import msalInstance from '../auth/msalInstance';

// Deliberately not importing axios's own `InternalAxiosRequestConfig` here:
// this file's `http` instance is built by @piaas/piaas-ui-toolkit, which
// carries its own (peer-satisfied, but locally a separate copy during
// file:-linked development) axios types. Typing this structurally avoids a
// nominal mismatch between the two - see the toolkit's README for why axios
// is a peer dependency in the first place.
interface RequestBodyConfig {
  method?: string;
  data?: unknown;
}

const PUBLIC_ROUTES = ['/user/activate', '/new-password', '/reset-password'];

const isPublicRoute = (): boolean =>
  PUBLIC_ROUTES.some(route => {
    const { pathname } = window.location;

    return pathname === route || pathname.startsWith(`${route}/`);
  });

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === '[object Object]';

const hasTransactionId = (data: unknown): boolean => {
  if (data instanceof FormData) {
    return data.has('transactionId');
  }

  if (data instanceof URLSearchParams) {
    return data.has('transactionId');
  }

  return isPlainObject(data) && typeof data.transactionId !== 'undefined';
};

const addTransactionId = (config: RequestBodyConfig): void => {
  const transactionId = uuidv4();
  const data: unknown = config.data;

  if (hasTransactionId(data)) {
    return;
  }

  if (data instanceof FormData) {
    data.append('transactionId', transactionId);
    return;
  }

  if (data instanceof URLSearchParams) {
    data.append('transactionId', transactionId);
    return;
  }

  if (isPlainObject(data)) {
    config.data = {
      ...data,
      transactionId,
    };
    return;
  }

  if (data === null || data === undefined) {
    config.data = { transactionId };
  }
};

// Only a real MSAL interaction requirement means the user's session is actually
// invalid. Network failures (e.g. an API being unreachable) must never trigger a
// login redirect - that would bounce the page in a loop while a backend is down.
const requiresLoginRedirect = (error: unknown): boolean =>
  error instanceof InteractionRequiredAuthError;

export const createAuthorizationHeaders = async (): Promise<HeadersInit | undefined> => {
  if (isPublicRoute()) return undefined;

  const account = msalInstance.getActiveAccount();
  if (!account) return undefined;

  const { accessToken } = await msalInstance.acquireTokenSilent({ ...loginRequest, account });

  if (!accessToken) {
    return undefined;
  }

  return {
    Authorization: `Bearer ${accessToken}`,
  };
};

export const refreshAuthToken = async (): Promise<void> => {
  if (isPublicRoute()) {
    return;
  }

  const account = msalInstance.getActiveAccount();
  if (!account) return;

  await msalInstance.acquireTokenSilent({ ...loginRequest, account });
};

// The toolkit's request interceptor already gates this on PUBLIC_ROUTES/skipAuth
// via `publicPaths`, so it doesn't need to re-check the route itself here.
const getAccessToken = async (): Promise<string | undefined> => {
  const account = msalInstance.getActiveAccount();
  if (!account) return undefined;

  try {
    const { accessToken } = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return accessToken;
  } catch (error: unknown) {
    if (requiresLoginRedirect(error)) {
      await startLoginRedirect(msalInstance, loginRequest);
    }
    throw error instanceof Error ? error : new Error('Failed to acquire an access token.');
  }
};

const UNAUTHORIZED_REDIRECT_KEY = 'piaas.unauthorizedRedirectAt';
const UNAUTHORIZED_REDIRECT_WINDOW_MS = 60_000;

// A 401 that survives a fresh login (e.g. the API rejects the token) would
// otherwise redirect forever. sessionStorage outlives the redirect round trip,
// so a second 401 within the window is surfaced as an error instead.
const claimUnauthorizedRedirect = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(UNAUTHORIZED_REDIRECT_KEY));
    const now = Date.now();

    if (last && now - last < UNAUTHORIZED_REDIRECT_WINDOW_MS) return false;

    sessionStorage.setItem(UNAUTHORIZED_REDIRECT_KEY, String(now));
  } catch (error) {
    // Storage unavailable: allow the redirect.
    console.error('Failed to access sessionStorage for the login redirect guard:', error);
  }

  return true;
};

// Called once the toolkit's own 401 retry (re-acquire token, retry once) has
// still failed, or couldn't be attempted at all (e.g. no account to begin
// with) - the session is effectively dead either way, so start a coordinated
// interactive login.
const onUnauthorized = async (): Promise<void> => {
  if (!claimUnauthorizedRedirect()) {
    console.warn('Skipping login redirect: a 401 persisted right after a recent redirect.');
    return;
  }

  try {
    await startLoginRedirect(msalInstance, loginRequest);
  } catch (error) {
    console.error('Failed to initiate login redirect:', error);
  }
};

const http = createHttpClient({
  getAccessToken,
  publicPaths: PUBLIC_ROUTES,
  onUnauthorized,
});

// Not generic to every micro-frontend - this is piaas-ui's own API contract,
// layered on top of the shared client rather than baked into the toolkit.
http.interceptors.request.use(
  config => {
    if (config.method?.toLowerCase() !== 'get') {
      addTransactionId(config);
    }

    return config;
  },
  (error: { message: string }) => Promise.reject(new Error(error.message))
);

export default http;
